/** Shared fire heatmap ramp — map layer and the incident-panel replay stay in lockstep. */

import { S3_SOURCE } from "./s3-list";

/** Full warmth after the pixel — long enough that the next polar/MTG slot still overlaps. */
export const HEAT_LIVE_MS = 3 * 3600 * 1000;
/**
 * Latent trail after that. VIIRS (~375 m) and MTG/MODIS/S3 (~1 km) sit on
 * different grids; keeping older footprints warm lets the kernels blend
 * instead of reading as a checkerboard. Window length does not change this.
 */
export const HEAT_FADE_MS = 14 * 3600 * 1000;

/** 1 at the playhead, 0 once the pixel is older than HEAT_FADE_MS. */
export function heatFade(ageMs: number): number {
  if (!Number.isFinite(ageMs) || ageMs < 0) return 0;
  if (ageMs <= HEAT_LIVE_MS) return 1;
  if (ageMs >= HEAT_FADE_MS) return 0;
  const t = (ageMs - HEAT_LIVE_MS) / (HEAT_FADE_MS - HEAT_LIVE_MS);
  // Smoothstep — avoids a linear cliff at the live/fade boundary.
  return 1 - t * t * (3 - 2 * t);
}

export type HeatStop = { t: number; r: number; g: number; b: number; a: number };

export const HEAT_COLOR_STOPS: HeatStop[] = [
  { t: 0, r: 0, g: 0, b: 0, a: 0 },
  // Start the ember earlier — a few 4 MW VIIRS cells never reached 0.15 density.
  { t: 0.06, r: 160, g: 50, b: 12, a: 0.4 },
  { t: 0.35, r: 255, g: 100, b: 22, a: 0.64 },
  { t: 0.7, r: 255, g: 170, b: 40, a: 0.8 },
  { t: 1, r: 255, g: 240, b: 200, a: 0.95 },
];

/** Same piecewise FRP → weight as the MapLibre heatmap-weight expression. */
export function heatWeightFromFrp(frp: number): number {
  const x = Math.max(0, frp);
  if (x <= 0) return 0.28;
  if (x >= 150) return 1;
  // Floor used to be 0.1 — 3.8 MW (Wijk aan Zee) vanished on the map.
  if (x <= 20) return 0.28 + (x / 20) * 0.42;
  return 0.7 + ((x - 20) / 130) * 0.3;
}

/**
 * Sentinel-3 from EFFIS has no MW, but SLSTR already crossed a fire threshold.
 * Treat the cell like a nominal polar look (~8 MW) so it glows, not a 0 MW pinprick.
 */
export const S3_HEAT_FRP_PROXY = 8;

/** Number stored on a heat tuple for weighting — not a measured FRP for S3. */
export function heatFrpProxy(source: string, frp: number | null | undefined): number {
  if (source === S3_SOURCE) return S3_HEAT_FRP_PROXY;
  return frp ?? 0;
}

export function heatWeightForLook(source: string, frp: number | null | undefined): number {
  return heatWeightFromFrp(heatFrpProxy(source, frp));
}

export function heatColorAt(t: number): { r: number; g: number; b: number; a: number } {
  const x = Math.max(0, Math.min(1, t));
  let i = 1;
  while (i < HEAT_COLOR_STOPS.length && HEAT_COLOR_STOPS[i]!.t < x) i += 1;
  const hi = HEAT_COLOR_STOPS[i]!;
  const lo = HEAT_COLOR_STOPS[i - 1]!;
  const u = (x - lo.t) / Math.max(hi.t - lo.t, 1e-6);
  return {
    r: lo.r + (hi.r - lo.r) * u,
    g: lo.g + (hi.g - lo.g) * u,
    b: lo.b + (hi.b - lo.b) * u,
    a: lo.a + (hi.a - lo.a) * u,
  };
}

/** MapLibre `heatmap-color` expression built from HEAT_COLOR_STOPS. */
export function heatColorExpression(): unknown[] {
  const expr: unknown[] = ["interpolate", ["linear"], ["heatmap-density"]];
  for (const s of HEAT_COLOR_STOPS) {
    expr.push(s.t, `rgba(${s.r},${s.g},${s.b},${s.a})`);
  }
  return expr;
}

/** Wire tuple from /api/fires — parse once, then fade by playhead without Date.parse. */
export type HeatTuple = [number, number, number, string, string | null];

export type HeatPoint = {
  lon: number;
  lat: number;
  frp: number;
  /** observed_at as epoch ms — parsed on load, not per Play frame. */
  t: number;
  incidentId: string | null;
  baseWeight: number;
};

export type HeatFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: { frp: number; weight: number; incident_id: string | null };
};

/** Hottest cells kept per fire per UTC hour on the default map. */
export const HEAT_PER_INCIDENT_HOUR = 2;

/** ISO hour `2026-08-16T01` — same cut as SQL `substr(observed_at, 1, 13)`. */
export function heatHourBucket(observedAtIso: string): string {
  return observedAtIso.slice(0, 13);
}

/**
 * Keep the hottest looks in each fire-hour so Iberian 10-min MTG cannot
 * empty Italy. Mirrors the default /api/fires heat SQL.
 */
export function sampleHeatTuples(
  rows: HeatTuple[],
  perHour = HEAT_PER_INCIDENT_HOUR,
  limit = rows.length,
): HeatTuple[] {
  const groups = new Map<string, HeatTuple[]>();
  for (const row of rows) {
    const key = `${row[4] ?? ""}|${heatHourBucket(row[3])}`;
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }
  const kept: HeatTuple[] = [];
  for (const list of groups.values()) {
    list.sort((a, b) => b[2] - a[2]);
    kept.push(...list.slice(0, perHour));
  }
  kept.sort((a, b) => (a[3] < b[3] ? 1 : a[3] > b[3] ? -1 : 0));
  return kept.slice(0, limit);
}

/** Open-panel detections → heat points so a selected fire always embers. */
export function heatPointsFromDetections(
  dets: Array<{
    lon: number;
    lat: number;
    frp_mw?: number | null;
    observed_at: string;
    incident_id?: string | null;
    source: string;
  }>,
): HeatPoint[] {
  const tuples: HeatTuple[] = [];
  for (const d of dets) {
    tuples.push([d.lon, d.lat, heatFrpProxy(d.source, d.frp_mw), d.observed_at, d.incident_id ?? null]);
  }
  return parseHeatPoints(tuples);
}

/** Union two heat lists. Same cell (id + time + rounded lon/lat) wins once. */
export function mergeHeatPoints(base: HeatPoint[], extra: HeatPoint[]): HeatPoint[] {
  if (extra.length === 0) return base;
  if (base.length === 0) return extra;
  const seen = new Set<string>();
  const out: HeatPoint[] = [];
  for (const p of [...base, ...extra]) {
    const k = `${p.incidentId}|${p.t}|${p.lon.toFixed(5)}|${p.lat.toFixed(5)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

/** Parse the compact heat payload once when /api/fires lands. */
export function parseHeatPoints(heat: HeatTuple[]): HeatPoint[] {
  const out: HeatPoint[] = [];
  for (const [lon, lat, frp, observed, incidentId] of heat) {
    const t = Date.parse(observed);
    if (!Number.isFinite(t)) continue;
    out.push({
      lon,
      lat,
      frp,
      t,
      incidentId,
      baseWeight: heatWeightFromFrp(frp),
    });
  }
  return out;
}

/** Playhead-faded GeoJSON features. Dropped when fade ≤ 0.02 — same cut as the old applyData filter. */
export function heatFeatures(points: HeatPoint[], playheadMs: number): HeatFeature[] {
  const features: HeatFeature[] = [];
  for (const p of points) {
    const fade = heatFade(playheadMs - p.t);
    if (fade <= 0.02) continue;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lon, p.lat] },
      properties: {
        frp: p.frp,
        weight: p.baseWeight * fade,
        incident_id: p.incidentId,
      },
    });
  }
  return features;
}

/** Visible heat cells for one fire at this playhead — selection ring / fly-to. */
export function heatLonLatAtPlayhead(
  points: HeatPoint[],
  playheadMs: number,
  incidentId: string | null,
): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (const p of points) {
    if (incidentId && p.incidentId !== incidentId) continue;
    if (heatFade(playheadMs - p.t) <= 0.02) continue;
    pts.push([p.lon, p.lat]);
  }
  return pts;
}

/** Thin a High Sensitivity trail so Play does not setData 20k points every frame. */
export function decimateHeat(points: HeatPoint[], max: number): HeatPoint[] {
  if (points.length <= max) return points;
  const step = Math.ceil(points.length / max);
  const out: HeatPoint[] = [];
  for (let i = 0; i < points.length; i += step) out.push(points[i]!);
  return out;
}
